import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import RuleFolderOutlined from "@mui/icons-material/RuleFolderOutlined";
import { Box, Button, MenuItem, Select } from "@mui/material";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { loanFetchedDataSizerUploadsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";

// Same vocab the Add Sizer panel (DrilldownSidebarSizerUpload) sends — the classify endpoint
// keys the tracker by (type × stage), so these MUST match what uploads send or
// classified rows won't line up with uploaded ones.
const SIZER_TYPES = ["GUC", "RTL", "DSCR", "Other"];
const SIZER_STAGES = [
  "Intake Pricing",
  "Request Lock Pricing",
  "Update Lock Pricing",
  "Final Pricing for Closing",
];

const DOC_GROUP = "Terms & Sizer";

// A "Terms & Sizer" doc is already tracked if a sizer_uploads row points at the
// same file (by storage name, or the tracker's latest_upload_oid == doc id).
const buildTrackedKeys = (sizerUploads) => {
  const fsn = new Set();
  const oid = new Set();
  for (const row of sizerUploads || []) {
    if (row?.file_storage_name) fsn.add(row.file_storage_name);
    if (row?.latest_upload_oid) oid.add(String(row.latest_upload_oid));
  }
  return { fsn, oid };
};

/**
 * The loan's `Terms & Sizer` documents that aren't yet in the sizer tracker
 * (arrived via general upload / Box sync, so they carry no Type/Stage). Exported
 * so the Sizer tab can size the section + decide the empty state. Returns [] while
 * the tracker is still loading so already-tracked docs don't flash as candidates.
 */
export const getUnclassifiedSizerDocs = (loanDocuments, sizerUploads) => {
  if (sizerUploads == null) return [];
  const { fsn, oid } = buildTrackedKeys(sizerUploads);
  return (loanDocuments || []).filter(
    (d) =>
      d?.docGroup === DOC_GROUP &&
      !(d.file_storage_name && fsn.has(d.file_storage_name)) &&
      !(d._id && oid.has(String(d._id))),
  );
};

/**
 * "Needs classification" list — pick a Type + Stage and Classify to promote an
 * existing sizer document into the tracker (POST /files/classify_loan_sizer) with
 * no re-upload. Presentational: the tab passes the pre-computed `candidates` and
 * owns layout; this renders the interactive rows (returns null when empty).
 *
 * An interactive per-row form (two selects + a button), so it's a stacked list
 * rather than AG Grid.
 */
const SizerNeedsClassificationSection = ({ candidates }) => {
  const dispatch = useDispatch();
  const loanId = useSelector((state) => state.loanDrilldown?._id);
  const { classifyLoanSizer, getAllSizerUploadsByDealId } = useLoanFilesHook();

  const [drafts, setDrafts] = useState({}); // docId -> { type, stage }
  const [classifyingId, setClassifyingId] = useState(null);

  const setDraft = useCallback((docId, patch) => {
    setDrafts((prev) => ({ ...prev, [docId]: { ...prev[docId], ...patch } }));
  }, []);

  const handleClassify = useCallback(
    (doc) => {
      const draft = drafts[doc._id] || {};
      if (!draft.type || !draft.stage || !loanId) return;
      setClassifyingId(doc._id);
      classifyLoanSizer({
        dealId: loanId,
        payload: {
          sizerDocumentId: doc._id,
          sizerType: draft.type,
          sizerStage: draft.stage,
          extractSizerData: false,
        },
        onSuccessFn: () => {
          getAllSizerUploadsByDealId({
            dealId: loanId,
            onSuccessFn: (data) =>
              dispatch(loanFetchedDataSizerUploadsSet(data || [])),
          });
          setClassifyingId(null);
        },
        onFailFn: () => setClassifyingId(null),
      });
    },
    [drafts, loanId, classifyLoanSizer, getAllSizerUploadsByDealId, dispatch],
  );

  if (!candidates?.length) return null;

  return (
    <Box>
      <Txt
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "#64748b",
        }}
      >
        Needs Classification ({candidates.length})
      </Txt>
      <Txt sx={{ fontSize: 12, color: "#64748b", mt: 0.5, mb: 2 }}>
        Sizers that arrived as documents (uploaded directly or synced from Box).
        Tag each with a Type and Stage to add it to the tracker — no re-upload.
      </Txt>
      <Flx column g={1.5}>
        {candidates.map((doc) => {
          const draft = drafts[doc._id] || {};
          const busy = classifyingId === doc._id;
          const ctx = [
            doc.uploadedBy,
            doc.modifiedDate ? formatDateTime(doc.modifiedDate) : null,
            doc.box_folder_path,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <Box
              key={doc._id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                border: "1px solid #e3e9ec",
                borderRadius: "10px",
                p: 1.5,
                background: "#fff",
              }}
            >
              <Flx ac g={1} sx={{ minWidth: 0, flex: "1 1 260px" }}>
                <RuleFolderOutlined sx={{ fontSize: 20, color: "#64748b" }} />
                <Box sx={{ minWidth: 0 }}>
                  <Txt
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0f172a",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {doc.file_display_name || "Untitled document"}
                  </Txt>
                  {ctx && (
                    <Txt sx={{ fontSize: 11.5, color: "#64748b" }}>{ctx}</Txt>
                  )}
                </Box>
              </Flx>

              <Select
                size="small"
                displayEmpty
                value={draft.type || ""}
                onChange={(e) => setDraft(doc._id, { type: e.target.value })}
                sx={{ minWidth: 120, fontSize: 13 }}
                renderValue={(v) =>
                  v || <span style={{ color: "#94a3b8" }}>Type</span>
                }
              >
                {SIZER_TYPES.map((t) => (
                  <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                    {t}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                displayEmpty
                value={draft.stage || ""}
                onChange={(e) => setDraft(doc._id, { stage: e.target.value })}
                sx={{ minWidth: 190, fontSize: 13 }}
                renderValue={(v) =>
                  v || <span style={{ color: "#94a3b8" }}>Stage</span>
                }
              >
                {SIZER_STAGES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>

              <Button
                variant="contained"
                size="small"
                disableElevation
                loading={busy}
                disabled={!draft.type || !draft.stage || busy}
                onClick={() => handleClassify(doc)}
                sx={{ textTransform: "none" }}
              >
                Classify
              </Button>
            </Box>
          );
        })}
      </Flx>
    </Box>
  );
};

export default SizerNeedsClassificationSection;
